import { z } from 'zod';
import { ErrorResponse } from '@budgetx/shared';
import { ClientApiError } from './apiError';

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

// In prod we may host the API on a different origin than the SPA (e.g.
// api.budgetx.dk vs budgetx.dk). VITE_API_URL prepends the origin. Leave
// empty to keep same-origin behavior (works with vite's dev proxy).
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

function resolveUrl(path: string): string {
  if (!API_BASE) return path;
  if (/^https?:\/\//i.test(path)) return path;
  return API_BASE + path;
}

async function request<T>(
  method: Method,
  path: string,
  body: unknown,
  schema: z.ZodType<T> | undefined,
): Promise<T | null> {
  let res: Response;
  try {
    const init: RequestInit = {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };
    res = await fetch(resolveUrl(path), init);
  } catch (err) {
    throw new ClientApiError(0, 'network', err instanceof Error ? err.message : 'network error');
  }

  // 204 No Content — nothing to parse
  if (res.status === 204) return null;

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!res.ok) {
    if (isJson) {
      try {
        const raw = await res.json();
        const parsed = ErrorResponse.safeParse(raw);
        if (parsed.success) {
          throw new ClientApiError(
            res.status,
            parsed.data.error.code,
            parsed.data.error.message,
            parsed.data.error.details,
          );
        }
      } catch (err) {
        if (err instanceof ClientApiError) throw err;
        // fall through
      }
    }
    throw new ClientApiError(res.status, 'internal', `HTTP ${res.status}`);
  }

  if (!schema) return null;

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    throw new ClientApiError(res.status, 'parse', 'response body not valid JSON');
  }
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ClientApiError(
      res.status,
      'parse',
      'response body failed schema',
      parsed.error.issues,
    );
  }
  return parsed.data;
}

export function apiGet<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  return request('GET', path, undefined, schema) as Promise<T>;
}
export function apiPost<T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T> | undefined,
): Promise<T | null> {
  return request('POST', path, body, schema);
}
export function apiPatch<T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T> | undefined,
): Promise<T | null> {
  return request('PATCH', path, body, schema);
}
export function apiPut<T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T> | undefined,
): Promise<T | null> {
  return request('PUT', path, body, schema);
}
export function apiDelete(path: string): Promise<null> {
  return request('DELETE', path, undefined, undefined) as Promise<null>;
}
